import React, { useEffect, useState } from "react";
import { Layout, Menu, Typography, Tooltip } from "antd";
import {
  ControlOutlined,
  PlayCircleOutlined,
  HistoryOutlined,
  LineChartOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  MessageOutlined,
  LogoutOutlined,
  LeftOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  DownloadOutlined,
  CrownOutlined,
  StarFilled,
} from "@ant-design/icons";
import { useInstall } from "./InstallProvider";
import ContactSupportFAQ from "./ContactSupportFAQ";

const { Sider } = Layout;
const { Title } = Typography;

function Sidebar({
  isMobile,
  siderCollapsed,
  setSiderCollapsed,
  activeKey,
  onMenuSelect,
  FTP,
  isMute,
  backendBaseUrl,
  title,
  slogan,
  logo,
  username,
  isAdmin,
  isPremium,
}) {
  const isCollapsed = siderCollapsed;
  const [hoverLock, setHoverLock] = useState(false);
  const { isStandalone, promptInstall } = useInstall();
  const [isInstalled, setIsInstalled] = useState(false);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);

  // Expose navigation function globally for HomeTrainer
  useEffect(() => {
    window.performNavigation = onMenuSelect;
    return () => {
      delete window.performNavigation;
    };
  }, [onMenuSelect]);

  useEffect(() => {
    // Try to detect if the PWA is installed even when we're in a browser tab
    (async () => {
      try {
        // Supported on Chromium (desktop/mobile)
        if (typeof navigator !== "undefined" && "getInstalledRelatedApps" in navigator) {
          const apps = await navigator.getInstalledRelatedApps();
          if (Array.isArray(apps) && apps.length > 0) {
            setIsInstalled(true);
          }
        }
      } catch {
        // no-op
      }
    })();
  }, []);

  const handleInstallClick = async () => {
    if (isStandalone) {
      window.alert("SimplyRide is already installed.");
      return;
    }
    const result = await promptInstall();
    if (!result || result.status === "unavailable" || result.status === "error") {
      // Fallback guidance
      window.alert(
        'Install not available or the app is already installed.\n\nTip: In Chrome/Edge desktop, open the menu and choose "Install app".'
      );
    }
  };

  const handleOpenAppClick = () => {
    // Prefer navigating to the app's start_url to trigger link capturing (if enabled)
    try {
      window.location.assign("/"); // must be within manifest scope
    } catch {
      // no-op
    }
    // Soft fallback via custom protocol (if supported by the browser)
    try {
      setTimeout(() => {
        const proto = "web+simplyride:open?src=browser";
        const a = document.createElement("a");
        a.href = proto;
        a.rel = "noreferrer";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, 50);
    } catch {
      // no-op
    }
  };

  const menuItems = !isMobile
    ? [
      {
        key: "1",
        label: "Training Plan",
        icon: <ControlOutlined />,
      },
      {
        key: "2",
        label: FTP ? "SimplyRide" : "SimplyRide! (Please enter your FTP)",
        icon: <PlayCircleOutlined />,
        disabled: !FTP,
      },
      { key: "4", label: "History", icon: <HistoryOutlined /> },
      { key: "5", label: "Data Analysis (beta)", icon: <LineChartOutlined /> },
      ...(isAdmin ? [
        { key: "6", label: "Admin Dashboard", icon: <CrownOutlined /> },
        { key: "7", label: "Workout Parser", icon: <DownloadOutlined /> }
      ] : []),
    ]
    : [
      {
        key: "3",
        label: "SimpyRide",
        icon: <PlayCircleOutlined />,
        disabled: !FTP,
      },
      { key: "4", label: "History", icon: <HistoryOutlined /> },
      { key: "5", label: "Data Analysis (beta)", icon: <LineChartOutlined /> },
      ...(isAdmin ? [
        { key: "6", label: "Admin Dashboard", icon: <CrownOutlined /> },
        { key: "7", label: "Workout Parser", icon: <DownloadOutlined /> }
      ] : []),
    ];

  // Everything starting from the divider after "Contact" is pinned at the bottom
  const bottomPinnedMenuItems = [
    {
      key: "footer_contact_support",
      icon: <QuestionCircleOutlined  />,
      label: isCollapsed ? null : "Contact - Support - FAQ",
    },
    {
      key: "control_feedback",
      icon: <MessageOutlined />,
      label: isCollapsed ? null : "Feedback",
    },
    {
      key: "control_stable",
      icon: <SafetyCertificateOutlined style={{ color: "rgba(0,0,0,0.85)" }} />,
      label:
        isCollapsed
          ? null
          : (
              <a
                href="https://stable.simplyride.app"
                target="_blank"
                rel="noreferrer"
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <span>Latest stable version</span>
                <Tooltip title="Having trouble with this update? Open the latest stable release. Your existing login works there too.">

                <span role="img" aria-label="info" style={{ fontSize: 12, opacity: 0.8 }}>ℹ️</span>
                </Tooltip>

              </a>
          ),
    },
    {
      key: "control_install_app",
      icon: <DownloadOutlined />,
      label: isCollapsed
        ? null
        : isStandalone
          ? "Installed"
          : isInstalled
            ? "Open App"
            : "Install Desktop App",
      disabled: isStandalone,
    },
    { type: "divider" },

    {
      key: "control_profile",
      icon: <UserOutlined />,
      label: isCollapsed ? null : "Profile",
    },
    {
      key: "control_logout",
      icon: <LogoutOutlined />,
      label: isCollapsed ? null : "Logout",
      className: "logout-menu-item",
      onClick: () => {
        console.log("Logout button direct onClick triggered!");
        // Check if we're navigating away from training page during active session
        const isOnTrainingPage = activeKey === "2" || activeKey === "3";
        if (isOnTrainingPage) {
          // Use event system to check with HomeTrainer
          let shouldNavigate = true;
          const checkNavigation = (allowed) => {
            shouldNavigate = allowed;
          };

          window.dispatchEvent(new CustomEvent('navigationAttempt', {
            detail: { navigationKey: "control_logout", callback: checkNavigation }
          }));

          // Wait a bit for the event to be processed
          setTimeout(() => {
            if (!shouldNavigate) {
              return; // Navigation blocked, warning modal shown
            }
            onMenuSelect("control_logout");
          }, 10);
          return;
        }
        onMenuSelect("control_logout");
      },
    },
    { type: "divider" },
    {
      key: "footer_made",
      icon: null,
      disabled: true,
      style: {
        whiteSpace: "normal",
        height: "auto",
        lineHeight: 1.2,
        paddingTop: 8,
        paddingBottom: 8,
        overflow: "visible",
        marginBottom: 12,
      },
      label:
        isCollapsed ? (
          <div aria-hidden="true" style={{ height: 72 }} />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              padding: "6px 0",
              marginTop: "24px",
              minHeight: 72,
              textAlign: "center",
            }}
          >
            <span>
              Made with <span style={{ color: "red", margin: "0 4px" }}>❤</span> by Sam - v2.0
            </span>
            
          </div>
        ),
    },
  ];

  return (
    <Sider
      theme="light"
      width={260}
      collapsedWidth={64}
      collapsible
      collapsed={isCollapsed}
      trigger={null}
      onMouseEnter={() => {
        if (!isMobile && isCollapsed && !hoverLock) setSiderCollapsed(false);
      }}
      onMouseLeave={() => {
        if (!isMobile && !isCollapsed && !hoverLock) setSiderCollapsed(true);
        if (hoverLock) setHoverLock(false);
      }}
      className="glass-panel app-sider"
      style={{
        padding: "16px 8px",
        overflow: "auto",
        height: "100vh",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Content */}
        <div>
          <div style={{ textAlign: "center", marginBottom: isCollapsed ? 40 : 16 }}>
            <img
              src={logo}
              style={{
                height: isMobile ? 44 : isCollapsed ? 36 : 44,
                marginBottom: 8,
              }}
              alt={title}
            />
            {!isCollapsed ? (
              <div style={{ minHeight: 48 }}>
                <Title level={4} style={{ color: "#031932", margin: 0 }}>
                  {title}
                </Title>
                <div style={{ lineHeight: 1 }}>
                  <small style={{ color: "grey", fontSize: "11px" }}>{slogan}</small>
                 
                </div>
                {username && (
                  <div style={{ lineHeight: 1, marginTop: 4 }}>
                    <small
                      style={{ color: "#031932", fontSize: "12px", cursor: "pointer", textDecoration: "underline" }}
                      title="Go to profile"
                      onClick={() => {
                        // Check if we're navigating away from training page during active session
                        const isOnTrainingPage = activeKey === "2" || activeKey === "3";
                        if (isOnTrainingPage) {
                          // Use event system to check with HomeTrainer
                          let shouldNavigate = true;
                          const checkNavigation = (allowed) => {
                            shouldNavigate = allowed;
                          };

                          window.dispatchEvent(new CustomEvent('navigationAttempt', {
                            detail: { navigationKey: "profile", callback: checkNavigation }
                          }));

                          // Wait a bit for the event to be processed
                          setTimeout(() => {
                            if (!shouldNavigate) {
                              return; // Navigation blocked, warning modal shown
                            }
                            onMenuSelect("profile");
                          }, 10);
                          return;
                        }
                        onMenuSelect("profile");
                      }}
                    >
                    Hi {username} {isPremium ? <StarFilled style={{ color: "#fadb14", marginLeft: 6 }} /> : null}
                    </small>
                  </div>
                )}
              </div>
            ) : <div style={{ height: 48 }} />}
          </div>
          <Menu
            mode="inline"
            selectedKeys={[activeKey]}
            items={menuItems}
            onClick={({ key }) => {
              // Check if we're navigating away from training page during active session
              const isOnTrainingPage = activeKey === "2" || activeKey === "3";
              const isNavigatingAway = key !== activeKey; // Check for any navigation away from training

              if (isOnTrainingPage && isNavigatingAway) {
                // Use event system to check with HomeTrainer
                let shouldNavigate = true;
                const checkNavigation = (allowed) => {
                  shouldNavigate = allowed;
                };

                window.dispatchEvent(new CustomEvent('navigationAttempt', {
                  detail: { navigationKey: key, callback: checkNavigation }
                }));

                // Wait a bit for the event to be processed
                setTimeout(() => {
                  if (!shouldNavigate) {
                    return; // Navigation blocked, warning modal shown
                  }
                  onMenuSelect(key);
                }, 10);
                return;
              }
              onMenuSelect(key);
            }}
          />
        </div>
        {/* Bottom section: stays at bottom when there is room; scrolls when not */}
        <div style={{ marginTop: "auto", borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <Menu
            mode="inline"
            selectedKeys={[]}
            items={bottomPinnedMenuItems}
            onClick={({ key, domEvent }) => {
              console.log("Menu item clicked:", key);
              if (key === "footer_contact_support") {
                domEvent.preventDefault();
                setContactSupportOpen(true);
                return;
              }
              if (key === "control_stable") {
                domEvent.preventDefault();
                window.open("https://stable.simplyride.app", "_blank", "noopener,noreferrer");
                return;
              }
              if (key === "control_install_app") {
                domEvent.preventDefault();
                if (!isStandalone && isInstalled) {
                  handleOpenAppClick();
                } else {
                  handleInstallClick();
                }
                return;
              }

              // Check if we're navigating away from training page during active session
              const isOnTrainingPage = activeKey === "2" || activeKey === "3";
              const isNavigationKey = ["control_profile", "control_logout", "4", "5", "6"].includes(key);
              const isModalOnly = ["control_feedback", "footer_contact_support"].includes(key);

              if (isOnTrainingPage && isNavigationKey && !isModalOnly) {
                // Use event system to check with HomeTrainer
                let shouldNavigate = true;
                const checkNavigation = (allowed) => {
                  shouldNavigate = allowed;
                };

                window.dispatchEvent(new CustomEvent('navigationAttempt', {
                  detail: { navigationKey: key, callback: checkNavigation }
                }));

                // Wait a bit for the event to be processed
                setTimeout(() => {
                  if (!shouldNavigate) {
                    return; // Navigation blocked, warning modal shown
                  }
                  onMenuSelect(key);
                }, 10);
                return;
              }

              onMenuSelect(key);
            }}
          />
          {/* Collapse / Expand control */}
          {!isCollapsed && (
            <div style={{ paddingTop: 8, paddingBottom: 4, display: "flex", justifyContent: "center" }}>
              <div
                role="button"
                aria-label="Collapse sidebar"
                title="Collapse sidebar"
                onClick={() => {
                  setHoverLock(true);
                  setSiderCollapsed(true);
                }}
                style={{
                  cursor: "pointer",
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.3)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              >
                <LeftOutlined style={{ color: "rgba(0,0,0,0.65)" }} />
              </div>
            </div>
          )}
          {isCollapsed && (
            <div style={{ paddingTop: 8, paddingBottom: 4, display: "flex", justifyContent: "center" }}>
              <div
                role="button"
                aria-label="Expand sidebar"
                title="Expand sidebar"
                onClick={() => setSiderCollapsed(false)}
                style={{
                  cursor: "pointer",
                  width: 36,
                  height: 36,
                  borderRadius: 18,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.3)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                }}
              >
                <RightOutlined style={{ color: "rgba(0,0,0,0.65)" }} />
              </div>
            </div>
          )}
        </div>
      </div>
      <ContactSupportFAQ
        open={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
        backendBaseUrl={backendBaseUrl}
        onOpenFeedback={() => onMenuSelect && onMenuSelect("control_feedback")}
        onOpenBug={() => onMenuSelect && onMenuSelect("control_bug")}
      />
    </Sider>
  );
}

export default Sidebar;

